import React, { useEffect, useRef, useState } from "react";

/**
 * Premium custom cursor
 * - Small dot + trailing ring
 * - Expands on [data-cursor="button"] or buttons/links
 * - Click ripple
 * - Auto-disabled on coarse pointers (mobile/tablet)
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [ripples, setRipples] = useState([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isFine = window.matchMedia("(pointer: fine)").matches;
    if (!isFine) return;
    setEnabled(true);
    document.documentElement.classList.add("has-custom-cursor");

    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const ring = { x: mouse.x, y: mouse.y };
    let rafId;
    let hovering = false;
    let pressed = false;

    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      const target = e.target;
      const hot = target?.closest?.(
        'a, button, [role="button"], input, select, textarea, [data-cursor="button"]'
      );
      hovering = !!hot;
    };

    const onDown = (e) => {
      pressed = true;
      const id = Date.now() + Math.random();
      setRipples((r) => [...r, { id, x: e.clientX, y: e.clientY }]);
      setTimeout(() => {
        setRipples((r) => r.filter((rr) => rr.id !== id));
      }, 650);
    };
    const onUp = () => { pressed = false; };
    const onLeave = () => {
      if (dotRef.current) dotRef.current.style.opacity = "0";
      if (ringRef.current) ringRef.current.style.opacity = "0";
    };
    const onEnter = () => {
      if (dotRef.current) dotRef.current.style.opacity = "1";
      if (ringRef.current) ringRef.current.style.opacity = "1";
    };

    const tick = () => {
      ring.x += (mouse.x - ring.x) * 0.18;
      ring.y += (mouse.y - ring.y) * 0.18;

      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${mouse.x}px, ${mouse.y}px, 0) translate(-50%, -50%) scale(${pressed ? 0.7 : 1})`;
      }
      if (ringRef.current) {
        const scale = hovering ? 1.9 : pressed ? 0.85 : 1;
        ringRef.current.style.transform = `translate3d(${ring.x}px, ${ring.y}px, 0) translate(-50%, -50%) scale(${scale})`;
        ringRef.current.style.borderColor = hovering
          ? "rgba(167, 139, 250, 0.9)"
          : "rgba(255, 255, 255, 0.5)";
        ringRef.current.style.backgroundColor = hovering
          ? "rgba(139, 92, 246, 0.12)"
          : "transparent";
        ringRef.current.style.boxShadow = hovering
          ? "0 0 26px rgba(139,92,246,0.55), 0 0 8px rgba(236,72,153,0.35)"
          : "0 0 14px rgba(139,92,246,0.22)";
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.documentElement.classList.remove("has-custom-cursor");
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        data-testid="custom-cursor-ring"
        className="pointer-events-none fixed z-[9998] w-8 h-8 rounded-full border will-change-transform"
        style={{
          left: 0,
          top: 0,
          transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 200ms ease",
          mixBlendMode: "screen",
        }}
      />
      <div
        ref={dotRef}
        data-testid="custom-cursor-dot"
        className="pointer-events-none fixed z-[9999] w-[6px] h-[6px] rounded-full will-change-transform"
        style={{
          left: 0,
          top: 0,
          background: "radial-gradient(circle at center, #fff 0%, #a78bfa 70%, #ec4899 100%)",
          boxShadow: "0 0 10px rgba(167,139,250,0.9)",
        }}
      />
      {ripples.map((r) => (
        <span
          key={r.id}
          className="pointer-events-none fixed z-[9997] w-6 h-6 rounded-full"
          style={{
            left: r.x,
            top: r.y,
            transform: "translate(-50%, -50%)",
            animation: "cursor-ripple 600ms ease-out forwards",
            background: "radial-gradient(circle, rgba(167,139,250,0.45), rgba(236,72,153,0.15) 60%, transparent 70%)",
          }}
        />
      ))}
    </>
  );
}
